import { useEffect, useRef, useState, type FormEvent } from "react";
import { apiRequest } from "@kira/api-client";
import type { Order, Product } from "@kira/contracts";
import "./market-operations.css";
const yuan = (n: number) => `¥${(n / 100).toFixed(2)}`;
const date = (s: string) =>
  new Date(s).toLocaleString("zh-CN", { hour12: false });
function useResource<T>(path: string) {
  const [data, setData] = useState<T | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const version = useRef(0);
  async function load() {
    const v = ++version.current;
    setError("");
    try {
      const result = await apiRequest<T>(path);
      if (v === version.current) setData(result);
    } catch (e) {
      if (v === version.current)
        setError(e instanceof Error ? e.message : "加载失败");
    }
  }
  useEffect(() => {
    void load();
    const refresh = () => void load();
    window.addEventListener("kira:market-updated", refresh);
    return () => {
      version.current++;
      window.removeEventListener("kira:market-updated", refresh);
    };
  }, [path]);
  async function mutate(
    url: string,
    body: unknown,
    method: "POST" | "PUT" = "POST",
  ) {
    if (busy) return false;
    setBusy(true);
    setError("");
    try {
      await apiRequest(url, { method, body });
      await load();
      window.dispatchEvent(new Event("kira:market-updated"));
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "操作失败");
      return false;
    } finally {
      setBusy(false);
    }
  }
  return { data, error, busy, load, mutate };
}
function Status({
  error,
  loading,
  retry,
}: {
  error: string;
  loading: boolean;
  retry: () => unknown;
}) {
  return error ? (
    <p role="alert">
      {error} <button onClick={() => void retry()}>重试加载</button>
    </p>
  ) : loading ? (
    <p role="status">正在加载…</p>
  ) : null;
}
type OwnProduct = Pick<
  Product,
  | "id"
  | "title"
  | "imageUrl"
  | "stock"
  | "sales"
  | "deadline"
  | "status"
  | "closeReason"
> & { isCarpool: number };
type Wallet = {
  availableCents: number;
  pendingCents: number;
  frozenCents: number;
  cards: { id: string; bankName: string; lastFour: string }[];
  entries: { id: string; amountCents: number; createdAt: string }[];
};
export function SellerOverview({
  orders,
  onPanel,
}: {
  orders: Order[];
  onPanel: (panel: "wallet" | "surveys" | "after-sale") => void;
}) {
  const wallet = useResource<Wallet>("/api/market/wallet");
  const latestOrders = useResource<Order[]>("/api/market/seller/orders");
  const today = new Date().toLocaleDateString("en-CA", {
    timeZone: "Asia/Shanghai",
  });
  const todays = (latestOrders.data ?? orders).filter(
    (o) =>
      new Date(o.createdAt).toLocaleDateString("en-CA", {
        timeZone: "Asia/Shanghai",
      }) === today,
  );
  return (
    <section className="market-ops">
      <h2>卖家管理中心</h2>
      <div className="market-ops__metrics">
        <article>
          <span>今日订单</span>
          <strong>{todays.length}</strong>
          <small>按北京时间统计</small>
        </article>
        <article>
          <span>今日成交</span>
          <strong>
            {yuan(
              todays
                .filter((o) => !["pending", "refunded"].includes(o.status))
                .reduce((n, o) => n + o.totalCents, 0),
            )}
          </strong>
          <small>待结算 {yuan(wallet.data?.pendingCents ?? 0)}</small>
        </article>
      </div>
      <div className="market-ops__links">
        <button onClick={() => onPanel("wallet")}>我的钱包</button>
        <button onClick={() => onPanel("surveys")}>新品调研</button>
        <button onClick={() => onPanel("after-sale")}>售后群</button>
      </div>
      <Status error={wallet.error} loading={!wallet.data} retry={wallet.load} />
    </section>
  );
}
export function CarpoolManagement({ onCreate }: { onCreate: () => unknown }) {
  const r = useResource<OwnProduct[]>("/api/market/seller/products");
  const [selected, setSelected] = useState<string | null>(null),
    [stock, setStock] = useState(""),
    [deadline, setDeadline] = useState("");
  const item = r.data?.find((p) => p.id === selected);
  return (
    <section className="market-ops">
      <header>
        <h2>我要发车</h2>
        <button onClick={() => void onCreate()}>新建拼车</button>
      </header>
      <p>
        到期自动截团；售罄可补货或立即截团。发车前 24
        小时锁定时间，改期会通知已上车买家。
      </p>
      <Status error={r.error} loading={!r.data} retry={r.load} />
      {r.data && !r.data.some((p) => p.isCarpool) ? (
        <p className="market-ops__empty">还没有发过车，创建第一趟拼车吧。</p>
      ) : null}
      {r.data
        ?.filter((p) => p.isCarpool)
        .map((p) => (
          <article className="market-ops__row" key={p.id}>
            <div>
              <strong>{p.title}</strong>
              <p>
                {p.deadline ? date(p.deadline) : "未设时间"} ·{" "}
                {p.status !== "active"
                  ? "已截团"
                  : p.stock === 0
                    ? "已售罄，等待卖家处理"
                    : "上车中"}
              </p>
              <small>
                已售 {p.sales} · 剩余 {p.stock}
              </small>
            </div>
            <button
              onClick={() => {
                setSelected(p.id);
                setStock(String(p.stock));
                setDeadline(
                  p.deadline
                    ? new Date(
                        Date.parse(p.deadline) -
                          new Date().getTimezoneOffset() * 60000,
                      )
                        .toISOString()
                        .slice(0, 16)
                    : "",
                );
              }}
            >
              管理
            </button>
          </article>
        ))}
      {item ? (
        <section className="market-ops__editor" aria-label="管理拼车">
          <header>
            <h3>{item.title}</h3>
            <button onClick={() => setSelected(null)}>收起</button>
          </header>
          {item.status !== "active" ? (
            <p>已经截团，可在订单管理中继续处理已上车订单。</p>
          ) : (
            <>
              <label>
                剩余库存
                <input
                  type="number"
                  min="0"
                  max="100000"
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                />
              </label>
              <button
                disabled={r.busy || !/^\d+$/.test(stock)}
                onClick={() =>
                  void r.mutate(`/api/market/carpools/${item.id}`, {
                    action: "stock",
                    stock: Number(stock),
                  })
                }
              >
                保存库存
              </button>
              <label>
                发车时间
                <input
                  type="datetime-local"
                  value={deadline}
                  disabled={
                    Date.parse(item.deadline ?? "") - Date.now() <= 86400000
                  }
                  onChange={(e) => setDeadline(e.target.value)}
                />
              </label>
              <small>
                {Date.parse(item.deadline ?? "") - Date.now() <= 86400000
                  ? "时间已锁定"
                  : "改期后的发车时间也须距现在超过 24 小时"}
              </small>
              <button
                disabled={
                  r.busy ||
                  !deadline ||
                  Date.parse(item.deadline ?? "") - Date.now() <= 86400000
                }
                onClick={() =>
                  void r.mutate(`/api/market/carpools/${item.id}`, {
                    action: "deadline",
                    deadline: new Date(deadline).toISOString(),
                  })
                }
              >
                保存改期并通知买家
              </button>
              <details>
                <summary>立即截团</summary>
                <p>截团后停止新买家上车，已有订单继续履约，此操作不能撤销。</p>
                <button
                  disabled={r.busy}
                  onClick={() =>
                    void r.mutate(`/api/market/carpools/${item.id}`, {
                      action: "close",
                    })
                  }
                >
                  确认立即截团
                </button>
              </details>
            </>
          )}
        </section>
      ) : null}
    </section>
  );
}
type Demand = {
  id: string;
  artistId: string | null;
  artistName: string;
  title: string;
  category: string;
  count: number;
  medianBudgetCents: number;
  inSale: boolean;
  followed: boolean;
  wishes: {
    id: string;
    content: string;
    budget_cents: number | null;
    created_at: string;
  }[];
};
export function DemandWall({
  onPublish,
}: {
  onPublish: (carpool: boolean) => unknown;
}) {
  const r = useResource<Demand[]>("/api/market/demands");
  const [filter, setFilter] = useState("all"),
    [selected, setSelected] = useState<string | null>(null);
  const groups = r.data?.filter(
    (g) => filter === "all" || (filter === "mine" ? g.inSale : !g.followed),
  );
  return (
    <section className="market-ops">
      <h2>需求墙</h2>
      <p>买家的愿望按艺人和类型聚合。查看预算与需求，再决定上架或发车。</p>
      <div className="market-ops__links">
        {[
          ["all", "全部"],
          ["mine", "我在售艺人"],
          ["pending", "待跟进"],
        ].map(([key, label]) => (
          <button
            key={key}
            aria-pressed={filter === key}
            onClick={() => setFilter(key!)}
          >
            {label}
          </button>
        ))}
      </div>
      <Status error={r.error} loading={!r.data} retry={r.load} />
      {groups?.length === 0 ? (
        <p className="market-ops__empty">暂无符合条件的需求。</p>
      ) : null}
      {groups?.map((g) => (
        <article className="market-ops__demand" key={g.id}>
          <button
            className="market-ops__row"
            onClick={() => setSelected(selected === g.id ? null : g.id)}
          >
            <div>
              <strong>
                {g.artistName} · {g.title}
              </strong>
              <p>
                {g.count} 人许愿 · 预算中位数{" "}
                {g.medianBudgetCents ? yuan(g.medianBudgetCents) : "未填写"}
              </p>
              <small>
                {g.inSale ? "我在售 · " : ""}
                {g.followed ? "已跟进" : "待跟进"}
              </small>
            </div>
            <span>›</span>
          </button>
          {selected === g.id ? (
            <div className="market-ops__editor">
              {g.wishes.map((w) => (
                <p key={w.id}>
                  {w.content}{" "}
                  <small>
                    预算{" "}
                    {w.budget_cents == null ? "未填写" : yuan(w.budget_cents)}
                  </small>
                </p>
              ))}
              <div className="market-ops__links">
                <button
                  disabled={r.busy}
                  onClick={() =>
                    void r.mutate(
                      `/api/market/demands/${encodeURIComponent(g.id)}`,
                      { followed: !g.followed },
                      "PUT",
                    )
                  }
                >
                  {g.followed ? "取消跟进" : "标记已跟进"}
                </button>
                <button onClick={() => void onPublish(false)}>去上架</button>
                <button onClick={() => void onPublish(true)}>去发车</button>
              </div>
            </div>
          ) : null}
        </article>
      ))}
    </section>
  );
}
export function SellerWallet() {
  const r = useResource<Wallet>("/api/market/wallet");
  const [bank, setBank] = useState(""),
    [tail, setTail] = useState(""),
    [card, setCard] = useState(""),
    [adding, setAdding] = useState(false);
  return (
    <section className="market-ops">
      <h2>我的钱包</h2>
      <p>演示结算：按订单状态计算，不进行真实银行转账。仅填写收款卡尾号。</p>
      <Status error={r.error} loading={!r.data} retry={r.load} />
      {r.data ? (
        <>
          <div className="market-ops__metrics">
            <article>
              <span>可结算</span>
              <strong>{yuan(r.data.availableCents)}</strong>
            </article>
            <article>
              <span>待结算 / 售后冻结</span>
              <strong>{yuan(r.data.pendingCents)}</strong>
              <small>{yuan(r.data.frozenCents)}</small>
            </article>
          </div>
          <h3>收款卡</h3>
          <details open={adding || r.data.cards.length === 0}>
            <summary
              onClick={(e) => {
                e.preventDefault();
                setAdding((v) => !v);
              }}
            >
              添加收款卡
            </summary>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void r
                  .mutate("/api/market/wallet/cards", {
                    bankName: bank,
                    lastFour: tail,
                  })
                  .then((ok) => {
                    if (ok) {
                      setBank("");
                      setTail("");
                      setAdding(false);
                    }
                  });
              }}
            >
              <label>
                银行名称
                <input
                  required
                  minLength={2}
                  maxLength={40}
                  value={bank}
                  onChange={(e) => setBank(e.target.value)}
                />
              </label>
              <label>
                卡号后四位
                <input
                  required
                  pattern="[0-9]{4}"
                  maxLength={4}
                  inputMode="numeric"
                  value={tail}
                  onChange={(e) => setTail(e.target.value)}
                />
              </label>
              <button disabled={r.busy}>添加演示收款卡</button>
            </form>
          </details>
          <label>
            选择收款卡
            <select
              aria-label="选择收款卡"
              value={card}
              onChange={(e) => setCard(e.target.value)}
            >
              <option value="">请选择</option>
              {r.data.cards.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.bankName} · 尾号 {c.lastFour}
                </option>
              ))}
            </select>
          </label>
          <button
            disabled={!card || r.busy || r.data.availableCents <= 0}
            onClick={() =>
              void r.mutate("/api/market/wallet/withdraw", { cardId: card })
            }
          >
            结算全部余额（演示）
          </button>
          <h3>结算记录</h3>
          {r.data.entries.length ? (
            r.data.entries.map((e) => (
              <p key={e.id}>
                {date(e.createdAt)} · {yuan(e.amountCents)} · 演示完成
              </p>
            ))
          ) : (
            <p>暂无结算记录，完成订单后可结算。</p>
          )}
        </>
      ) : null}
    </section>
  );
}
type Survey = {
  id: string;
  title: string;
  description: string;
  options: string[];
  status: string;
  isMine: boolean;
  sellerName: string;
  votes: { choice: number; count: number }[];
  mine: number | null;
};
export function MarketSurveys({ seller }: { seller: boolean }) {
  const r = useResource<Survey[]>("/api/market/surveys");
  const [title, setTitle] = useState(""),
    [description, setDescription] = useState(""),
    [options, setOptions] = useState(""),
    [creating, setCreating] = useState(false);
  return (
    <section className="market-ops">
      <h2>新品调研</h2>
      <p>了解款式、价格与数量偏好。每位买家每份调研保留一票，可修改选择。</p>
      <Status error={r.error} loading={!r.data} retry={r.load} />
      {seller ? (
        <details open={creating}>
          <summary
            onClick={(e) => {
              e.preventDefault();
              setCreating((v) => !v);
            }}
          >
            新建调研
          </summary>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void r
                .mutate("/api/market/surveys", {
                  title,
                  description,
                  options: options
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
                .then((ok) => {
                  if (ok) {
                    setTitle("");
                    setDescription("");
                    setOptions("");
                    setCreating(false);
                  }
                });
            }}
          >
            <label>
              调研标题
              <input
                required
                minLength={2}
                maxLength={80}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <label>
              说明
              <textarea
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            <label>
              选项（每行一个，2–6 项）
              <textarea
                required
                value={options}
                onChange={(e) => setOptions(e.target.value)}
              />
            </label>
            <button disabled={r.busy}>发布调研</button>
          </form>
        </details>
      ) : null}
      {r.data
        ?.filter((s) => !seller || s.isMine)
        .map((s) => (
          <article className="market-ops__editor" key={s.id}>
            <h3>{s.title}</h3>
            <p>{s.description}</p>
            <small>
              {s.sellerName} · {s.status === "active" ? "收集中" : "已结束"}
            </small>
            <div className="market-ops__choices">
              {s.options.map((option, index) => (
                <button
                  key={index}
                  aria-pressed={s.mine === index}
                  disabled={s.isMine || s.status !== "active" || r.busy}
                  onClick={() =>
                    void r.mutate(`/api/market/surveys/${s.id}`, {
                      choice: index,
                    })
                  }
                >
                  {option} ·{" "}
                  {s.votes.find((v) => v.choice === index)?.count ?? 0} 票
                  {s.mine === index ? " ✓" : ""}
                </button>
              ))}
            </div>
            {s.isMine && s.status === "active" ? (
              <button
                disabled={r.busy}
                onClick={() => void r.mutate(`/api/market/surveys/${s.id}`, {})}
              >
                结束调研
              </button>
            ) : null}
          </article>
        ))}
      {r.data && !r.data.some((s) => !seller || s.isMine) ? (
        <p>暂无调研。</p>
      ) : null}
    </section>
  );
}
type Support = {
  order: Order;
  messages: {
    id: string;
    authorName: string;
    content: string;
    createdAt: string;
  }[];
};
export function AfterSaleGroups() {
  const r = useResource<Support[]>("/api/market/support");
  const [selected, setSelected] = useState(""),
    [content, setContent] = useState("");
  const group = r.data?.find((g) => g.order.id === selected);
  useEffect(() => {
    const id = window.setInterval(() => void r.load(), 10000);
    return () => clearInterval(id);
  }, []);
  return (
    <section className="market-ops">
      <h2>售后群</h2>
      <p>按订单建立售后会话，仅本单买家与卖家可查看。</p>
      <Status error={r.error} loading={!r.data} retry={r.load} />
      {group ? (
        <>
          <button onClick={() => setSelected("")}>返回会话列表</button>
          <h3>{group.order.items.map((i) => i.title).join("、")}</h3>
          <div className="market-ops__messages">
            {group.messages.map((m) => (
              <article key={m.id}>
                <small>
                  {m.authorName} · {date(m.createdAt)}
                </small>
                <p>{m.content}</p>
              </article>
            ))}
            {!group.messages.length ? (
              <p>暂时没有消息，可在下方描述订单问题。</p>
            ) : null}
          </div>
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              void r
                .mutate(`/api/market/support/${group.order.id}`, { content })
                .then((ok) => {
                  if (ok) setContent("");
                });
            }}
          >
            <label>
              售后消息
              <textarea
                required
                maxLength={1000}
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </label>
            <button disabled={r.busy || !content.trim()}>发送</button>
          </form>
        </>
      ) : (
        r.data?.map((g) => (
          <button
            className="market-ops__row"
            key={g.order.id}
            onClick={() => setSelected(g.order.id)}
          >
            <div>
              <strong>{g.order.items.map((i) => i.title).join("、")}</strong>
              <p>
                {g.order.sellerName} · {g.messages.length} 条消息
              </p>
              <small>订单 {g.order.id.slice(-8)}</small>
            </div>
            <span>›</span>
          </button>
        ))
      )}
      {r.data?.length === 0 ? (
        <p>暂无订单，成交后可在这里联系交易对方。</p>
      ) : null}
    </section>
  );
}
